import { useEffect, useId, useState } from "react";
import { useSearchParams } from "react-router";
import { useMap } from "react-map-gl/mapbox";
import MoreVert from "@mui/icons-material/MoreVert";
import {
  IconButton,
  ListItem,
  ListItemButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { useAppDialog } from "@pepsico/layout";
import {
  deleteParamValue,
  updateParamValue,
  usePagePrefs,
} from "@pepsico/pref-permissions";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { noop } from "es-toolkit";

import { useGetGrowersGeoByLocation } from "api/growers-geo/growers-geo";
import type {
  GrowerGeoResponse,
  SiteGeoResponse,
  TrialGeoResponse,
} from "api/model";
import {
  getGetSitesGeoByLocationQueryKey,
  useGetSitesGeoByLocation,
  useUpdateSiteGeoBoundary,
} from "api/sites-geo/sites-geo";
import { useGetTrialsGeoByLocation } from "api/trials-geo/trials-geo";
import {
  SELECTED_MAP_ITEM_BGCOLOR,
  SELECTED_MAP_ITEM_BORDER_COLOR,
} from "components/PlotMap/utils/display";
import { fitMapToBoundary } from "components/PlotMap/utils/mapCameraUtils";
import {
  readSelectedGrowerGeoId,
  readSelectedSiteGeoId,
  readSelectedTrialGeoId,
} from "components/PlotMap/utils/selectionPrefs";
import { usePlotMap } from "context/PlotMap";
import {
  fromBoundaryMultiPolygon,
  getBoundaryAreaHectares,
  hasBoundaryGeoJson,
  isBoundarySelfIntersecting,
} from "context/PlotMap/boundaryUtils";
import { getTrialGeoLabel } from "context/PlotMap/plotGridGeoUtils";
import { GROWER_PREF_KEY, SITE_PREF_KEY, TRIAL_PREF_KEY } from "defs";
import { getApiErrorMessage } from "utils";

import SiteBoundaryEditor from "./SiteBoundaryEditor";

const getRemoveSiteBoundaryError = getApiErrorMessage(
  "Failed to remove site boundary.",
);

const getUnassociateSiteError = getApiErrorMessage(
  "Failed to unassociate site.",
);

const formatBoundaryArea = (areaHectares: number | null): string =>
  areaHectares != null ? `${areaHectares.toFixed(2)} ha` : "--";

const getSavedSiteAreaDisplay = (site: SiteGeoResponse): string => {
  if (site.boundary == null || !hasBoundaryGeoJson(site.boundary)) {
    return "--";
  }

  const ring = fromBoundaryMultiPolygon(site.boundary);
  if (ring == null) {
    return "--";
  }

  return formatBoundaryArea(getBoundaryAreaHectares(ring));
};

interface SiteBoundaryItemProps {
  siteGeoId: number;
}

const SiteBoundaryItem: React.FC<SiteBoundaryItemProps> = ({ siteGeoId }) => {
  const { setAppDialog } = useAppDialog();
  const { default: map } = useMap();
  const queryClient = useQueryClient();
  const menuId = useId();
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const isMenuOpen = menuAnchorEl != null;
  const [prefs, isReady] = usePagePrefs();
  const [, setSearchParams] = useSearchParams();
  const {
    boundary: {
      editingSiteGeoId,
      enterSiteBoundaryMode,
      isClosed,
      isDirty,
      isGrowerBoundaryMode,
      isSiteBoundaryMode,
      loadSaved,
      points,
    },
  } = usePlotMap();

  const locationUuid = (prefs.location as string | null | undefined) ?? "";
  const { data: growers = [] } = useGetGrowersGeoByLocation(
    { location_uuid: locationUuid },
    {
      query: {
        enabled: locationUuid !== "",
        select: (res): GrowerGeoResponse[] =>
          res.status === 200 ? res.data : [],
      },
    },
  );
  const { data: sites = [] } = useGetSitesGeoByLocation(undefined, {
    query: {
      select: (res): SiteGeoResponse[] => (res.status === 200 ? res.data : []),
    },
  });
  const { data: trials = [] } = useGetTrialsGeoByLocation(
    { location_uuid: locationUuid },
    {
      query: {
        enabled: locationUuid !== "",
        select: (res): TrialGeoResponse[] =>
          res.status === 200 ? res.data : [],
      },
    },
  );
  const { mutateAsync: updateSiteGeo } = useUpdateSiteGeoBoundary();
  const updatingCount = useIsMutating({
    mutationKey: ["updateSiteGeoBoundary"],
  });
  const isSaving = updatingCount > 0;

  const site = sites.find((item) => item.id === siteGeoId) ?? null;
  const linkedGrower =
    site?.grower_id == null
      ? null
      : (growers.find((grower) => grower.id === site.grower_id) ?? null);
  const hasLinkedGrowerBoundary = hasBoundaryGeoJson(linkedGrower?.boundary);
  const isEditing = isSiteBoundaryMode && editingSiteGeoId === siteGeoId;
  const hasSiteBoundary = hasBoundaryGeoJson(site?.boundary);
  const siteLabel = site?.site_label?.trim() || "Unlabelled Site Boundary";
  const isSelfIntersecting =
    isClosed && points.length >= 3 && isBoundarySelfIntersecting(points);
  const areaDisplay = isEditing
    ? formatBoundaryArea(
        isClosed && !isSelfIntersecting && points.length >= 3
          ? getBoundaryAreaHectares(points)
          : null,
      )
    : site == null
      ? "--"
      : getSavedSiteAreaDisplay(site);
  const canMutate =
    !isGrowerBoundaryMode &&
    !isSaving &&
    (!isSiteBoundaryMode || editingSiteGeoId === siteGeoId) &&
    !isDirty;
  const canStart = hasLinkedGrowerBoundary && canMutate;
  const selectedGrowerGeoId = readSelectedGrowerGeoId(
    prefs,
    growers.map((item) => item.id),
  );
  const selectedSiteGeoId = readSelectedSiteGeoId(
    prefs,
    sites.map((item) => item.id),
  );
  const isSelected = site != null && site.id === selectedSiteGeoId;
  const siteTrials =
    site == null ? [] : trials.filter((trial) => trial.site_id === site.id);
  const soleTrial = siteTrials.length === 1 ? siteTrials[0] : null;
  const selectedTrialGeoId = readSelectedTrialGeoId(
    prefs,
    trials.map((trial) => trial.id),
  );
  const trialCaption =
    soleTrial != null
      ? getTrialGeoLabel(soleTrial, soleTrial.id)
      : siteTrials.length > 1
        ? "Multiple Trials"
        : null;
  const selectedTrialBelongsToSite =
    selectedTrialGeoId != null &&
    siteTrials.some((trial) => trial.id === selectedTrialGeoId);

  const withTrialSelection = (params: URLSearchParams): URLSearchParams =>
    soleTrial == null
      ? deleteParamValue(params, TRIAL_PREF_KEY, prefs)
      : updateParamValue(params, TRIAL_PREF_KEY, prefs, String(soleTrial.id));

  const withoutSelectedSite = (params: URLSearchParams): URLSearchParams =>
    deleteParamValue(
      deleteParamValue(params, SITE_PREF_KEY, prefs),
      TRIAL_PREF_KEY,
      prefs,
    );

  const applySelectedSite = (params: URLSearchParams): URLSearchParams => {
    if (site == null) {
      return params;
    }

    let next = updateParamValue(params, SITE_PREF_KEY, prefs, String(site.id));
    if (site.grower_id != null && site.grower_id !== selectedGrowerGeoId) {
      next = updateParamValue(
        next,
        GROWER_PREF_KEY,
        prefs,
        String(site.grower_id),
      );
    }

    return withTrialSelection(next);
  };

  useEffect(() => {
    if (!isReady || !isSelected) {
      return;
    }

    if (soleTrial != null) {
      if (selectedTrialGeoId === soleTrial.id) {
        return;
      }

      setSearchParams(
        (prev) =>
          updateParamValue(prev, TRIAL_PREF_KEY, prefs, String(soleTrial.id)),
        { replace: true, state: { inPageNav: true } },
      );
      return;
    }

    if (selectedTrialGeoId == null || selectedTrialBelongsToSite) {
      return;
    }

    setSearchParams((prev) => deleteParamValue(prev, TRIAL_PREF_KEY, prefs), {
      replace: true,
      state: { inPageNav: true },
    });
  }, [
    isReady,
    isSelected,
    prefs,
    selectedTrialBelongsToSite,
    selectedTrialGeoId,
    setSearchParams,
    soleTrial,
  ]);

  const handleToggleSelected = () => {
    if (!isReady || site == null || isGrowerBoundaryMode) {
      return;
    }

    setSearchParams(
      (prev) =>
        isSelected ? withoutSelectedSite(prev) : applySelectedSite(prev),
      { replace: true, state: { inPageNav: true } },
    );
    if (!isSelected) {
      fitMapToBoundary(map, site.boundary);
    }
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleStart = () => {
    if (
      site == null ||
      !hasLinkedGrowerBoundary ||
      isGrowerBoundaryMode ||
      (isSiteBoundaryMode && isDirty)
    ) {
      return;
    }

    const savedPoints =
      site.boundary != null && hasBoundaryGeoJson(site.boundary)
        ? (fromBoundaryMultiPolygon(site.boundary) ?? [])
        : [];
    loadSaved(savedPoints, site.site_label?.trim() ?? "");
    if (!isSelected && isReady) {
      setSearchParams((prev) => applySelectedSite(prev), {
        replace: true,
        state: { inPageNav: true },
      });
      fitMapToBoundary(map, site.boundary);
    }
    enterSiteBoundaryMode(site.id);
  };

  const handleRemove = async () => {
    if (
      site == null ||
      isGrowerBoundaryMode ||
      (isSiteBoundaryMode && isDirty) ||
      isSaving
    ) {
      return;
    }

    try {
      const response = await updateSiteGeo({
        data: { boundary: null },
        siteGeoId: site.id,
      });
      if (response.status !== 200) {
        setAppDialog({
          confirmText: "Close",
          message: <>{getRemoveSiteBoundaryError(response.data)}</>,
          onConfirm: noop,
          open: true,
          title: "Error",
        });
        return;
      }

      await queryClient.invalidateQueries({
        queryKey: getGetSitesGeoByLocationQueryKey(),
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to remove site boundary.";
      setAppDialog({
        confirmText: "Close",
        message: <>{message}</>,
        onConfirm: noop,
        open: true,
        title: "Error",
      });
    }
  };

  const handleUnassociate = async () => {
    if (
      site == null ||
      isGrowerBoundaryMode ||
      (isSiteBoundaryMode && isDirty) ||
      isSaving
    ) {
      return;
    }

    try {
      const response = await updateSiteGeo({
        data: { boundary: null, grower_id: null },
        siteGeoId: site.id,
      });
      if (response.status !== 200) {
        setAppDialog({
          confirmText: "Close",
          message: <>{getUnassociateSiteError(response.data)}</>,
          onConfirm: noop,
          open: true,
          title: "Error",
        });
        return;
      }

      if (isSelected && isReady) {
        setSearchParams((prev) => withoutSelectedSite(prev), {
          replace: true,
          state: { inPageNav: true },
        });
      }
      await queryClient.invalidateQueries({
        queryKey: getGetSitesGeoByLocationQueryKey(),
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to unassociate site.";
      setAppDialog({
        confirmText: "Close",
        message: <>{message}</>,
        onConfirm: noop,
        open: true,
        title: "Error",
      });
    }
  };

  const handleEditFromMenu = () => {
    handleCloseMenu();
    handleStart();
  };

  const handleClearFromMenu = () => {
    handleCloseMenu();
    void handleRemove();
  };

  const handleUnassociateFromMenu = () => {
    handleCloseMenu();
    void handleUnassociate();
  };

  if (site == null) {
    return null;
  }

  return (
    <ListItem
      disablePadding
      sx={{
        alignItems: "stretch",
        flexDirection: "column",
        gap: 0.75,
      }}
    >
      <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
        <ListItemButton
          aria-pressed={isSelected}
          disabled={isGrowerBoundaryMode}
          onClick={handleToggleSelected}
          sx={{
            alignItems: "center",
            bgcolor: isSelected ? SELECTED_MAP_ITEM_BGCOLOR : undefined,
            borderBottomLeftRadius: isSelected ? 0 : 1,
            borderBottomRightRadius: 1,
            borderTopLeftRadius: isSelected ? 0 : 1,
            borderTopRightRadius: 1,
            flex: 1,
            gap: 2,
            justifyContent: "space-between",
            minWidth: 0,
            px: 1,
            ...(isSelected && {
              "&:hover": { bgcolor: SELECTED_MAP_ITEM_BGCOLOR },
              borderLeftColor: SELECTED_MAP_ITEM_BORDER_COLOR,
              borderLeftStyle: "solid",
              borderLeftWidth: 4,
            }),
          }}
        >
          <Stack sx={{ minWidth: 0 }}>
            <Stack direction="row" sx={{ alignItems: "baseline", minWidth: 0 }}>
              <Typography noWrap sx={{ minWidth: 0 }} variant="body1">
                {siteLabel}
              </Typography>
              <Typography sx={{ flexShrink: 0, ml: "0.25em" }} variant="body1">
                {`(${site.year})`}
              </Typography>
            </Stack>
            {trialCaption != null && (
              <Typography color="text.secondary" noWrap variant="body2">
                {trialCaption}
              </Typography>
            )}
          </Stack>
          <Typography aria-live="polite" sx={{ flexShrink: 0 }} variant="body2">
            {areaDisplay}
          </Typography>
        </ListItemButton>
        {!isEditing && (
          <>
            <IconButton
              aria-controls={isMenuOpen ? menuId : undefined}
              aria-expanded={isMenuOpen ? "true" : undefined}
              aria-haspopup="menu"
              aria-label={`Boundary actions for ${siteLabel}`}
              disabled={isGrowerBoundaryMode}
              onClick={handleOpenMenu}
              size="small"
            >
              <MoreVert fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={menuAnchorEl}
              id={menuId}
              onClose={handleCloseMenu}
              open={isMenuOpen}
            >
              {hasSiteBoundary ? (
                <>
                  <MenuItem
                    aria-label={`Edit boundary for ${siteLabel}`}
                    disabled={!canStart}
                    onClick={handleEditFromMenu}
                  >
                    Edit
                  </MenuItem>
                  <MenuItem
                    aria-label={`Clear boundary for ${siteLabel}`}
                    disabled={!canStart}
                    onClick={handleClearFromMenu}
                    sx={{ color: "error.main" }}
                  >
                    Clear
                  </MenuItem>
                </>
              ) : (
                <MenuItem
                  aria-label={`Add boundary for ${siteLabel}`}
                  disabled={!canStart}
                  onClick={handleEditFromMenu}
                >
                  Draw
                </MenuItem>
              )}
              <MenuItem
                aria-label={`Unassociate ${siteLabel} from grower`}
                disabled={!canMutate}
                onClick={handleUnassociateFromMenu}
                sx={{ color: "error.main" }}
              >
                Unassociate
              </MenuItem>
            </Menu>
          </>
        )}
      </Stack>
      {isEditing && <SiteBoundaryEditor />}
    </ListItem>
  );
};

export default SiteBoundaryItem;
